/** Orbit camera with pointer/wheel/touch controls. */

import {
  clamp,
  degToRad,
  mat4,
  mat4Invert,
  mat4LookAt,
  mat4Multiply,
  mat4PerspectiveJittered,
  vec3,
  vec3Set,
  type Mat4,
  type Vec3,
} from './mat4';

const MIN_PITCH = -Math.PI / 2 + 0.02;
const MAX_PITCH = Math.PI / 2 - 0.02;

export interface CameraView {
  viewProj: Mat4;
  invViewProj: Mat4;
  position: Vec3;
}

export class OrbitCamera {
  yaw = degToRad(-38);
  pitch = degToRad(24);
  distance = 2.9;
  minDistance = 0.35;
  maxDistance = 30;
  fov = 38;
  readonly target: Vec3 = vec3(0, 0, 0);

  private readonly view = mat4();
  private readonly proj = mat4();
  private readonly viewProj = mat4();
  private readonly invViewProj = mat4();
  private readonly position = vec3();
  private readonly up = vec3(0, 1, 0);

  /** Called whenever the view changes, so the renderer can reset accumulation. */
  onChange: (() => void) | null = null;

  private detachFns: Array<() => void> = [];

  computeView(aspect: number, jitterX = 0, jitterY = 0): CameraView {
    const cp = Math.cos(this.pitch);
    vec3Set(
      this.position,
      this.target[0] + this.distance * cp * Math.sin(this.yaw),
      this.target[1] + this.distance * Math.sin(this.pitch),
      this.target[2] + this.distance * cp * Math.cos(this.yaw),
    );

    const near = Math.max(0.01, this.distance * 0.02);
    const far = this.distance + 40;
    mat4PerspectiveJittered(this.proj, degToRad(this.fov), aspect, near, far, jitterX, jitterY);
    mat4LookAt(this.view, this.position, this.target, this.up);
    mat4Multiply(this.viewProj, this.proj, this.view);
    mat4Invert(this.invViewProj, this.viewProj);

    return { viewProj: this.viewProj, invViewProj: this.invViewProj, position: this.position };
  }

  /** Pulls the camera back far enough to frame a sphere of the given radius. */
  frame(radius: number): void {
    const half = degToRad(this.fov) / 2;
    this.distance = clamp((radius / Math.tan(half)) * 1.12, this.minDistance, this.maxDistance);
    this.changed();
  }

  orbit(dx: number, dy: number): void {
    this.yaw -= dx;
    this.pitch = clamp(this.pitch + dy, MIN_PITCH, MAX_PITCH);
    this.changed();
  }

  pan(dx: number, dy: number): void {
    // Move the target in the camera's screen plane.
    const cp = Math.cos(this.pitch);
    const sp = Math.sin(this.pitch);
    const sy = Math.sin(this.yaw);
    const cy = Math.cos(this.yaw);

    const rightX = cy;
    const rightZ = -sy;
    const upX = -sp * sy;
    const upY = cp;
    const upZ = -sp * cy;

    const scale = this.distance * 0.9;
    this.target[0] += (-dx * rightX + dy * upX) * scale;
    this.target[1] += dy * upY * scale;
    this.target[2] += (-dx * rightZ + dy * upZ) * scale;
    this.changed();
  }

  zoom(factor: number): void {
    this.distance = clamp(this.distance * factor, this.minDistance, this.maxDistance);
    this.changed();
  }

  spin(radians: number): void {
    this.yaw += radians;
    this.changed();
  }

  reset(): void {
    this.yaw = degToRad(-38);
    this.pitch = degToRad(24);
    vec3Set(this.target, 0, 0, 0);
    this.changed();
  }

  /** Axis-aligned presets: 0 front (x/y), 1 top (x/t), 2 side (y/t), 3 three-quarter. */
  preset(index: number): void {
    const views: Array<[number, number]> = [
      [0, 0],
      [0, degToRad(89)],
      [degToRad(90), 0],
      [degToRad(-38), degToRad(24)],
    ];
    const [yaw, pitch] = views[clamp(index, 0, views.length - 1) | 0];
    this.yaw = yaw;
    this.pitch = pitch;
    vec3Set(this.target, 0, 0, 0);
    this.changed();
  }

  private changed(): void {
    this.onChange?.();
  }

  attach(element: HTMLElement): () => void {
    const pointers = new Map<number, { x: number; y: number }>();
    let pinchDistance = 0;

    const onPointerDown = (e: PointerEvent) => {
      element.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinchDistance = Math.hypot(a.x - b.x, a.y - b.y);
      }
      element.classList.add('dragging');
    };

    const onPointerMove = (e: PointerEvent) => {
      const prev = pointers.get(e.pointerId);
      if (!prev) return;
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.size >= 2) {
        const [a, b] = [...pointers.values()];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinchDistance > 0 && dist > 0) {
          this.zoom(pinchDistance / dist);
        }
        pinchDistance = dist;
        return;
      }

      const w = element.clientWidth || 1;
      const h = element.clientHeight || 1;
      if (e.shiftKey || e.buttons === 4) {
        this.pan(dx / w, dy / h);
      } else {
        this.orbit((dx / w) * Math.PI * 2, (dy / h) * Math.PI);
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinchDistance = 0;
      if (pointers.size === 0) element.classList.remove('dragging');
      if (element.hasPointerCapture(e.pointerId)) element.releasePointerCapture(e.pointerId);
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1;
      this.zoom(Math.exp(e.deltaY * unit * 0.0015));
    };

    const onContextMenu = (e: Event) => e.preventDefault();

    element.addEventListener('pointerdown', onPointerDown);
    element.addEventListener('pointermove', onPointerMove);
    element.addEventListener('pointerup', onPointerUp);
    element.addEventListener('pointercancel', onPointerUp);
    element.addEventListener('wheel', onWheel, { passive: false });
    element.addEventListener('contextmenu', onContextMenu);

    const detach = () => {
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('pointermove', onPointerMove);
      element.removeEventListener('pointerup', onPointerUp);
      element.removeEventListener('pointercancel', onPointerUp);
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('contextmenu', onContextMenu);
    };
    this.detachFns.push(detach);
    return detach;
  }

  dispose(): void {
    for (const fn of this.detachFns) fn();
    this.detachFns = [];
  }
}
